import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, Heart, Ratio } from 'lucide-react'
import { FloatIslandSheet } from '../ui/FloatIslandSheet'
import { ZoomableImage } from '../ui/ZoomableImage'
import type { VoxnelaTaskItem } from '../../data/voxnela56Tasks'
import { haptic } from '../../lib/native'
import { useUiText } from '../../i18n/ui'
import { studioMessages } from '../../i18n/studio'
import { useStyleFavorites } from '../../studio/lib/useStyleFavorites'
import '../../styles/task-showcase-modal.css'

export interface TaskShowcaseModalProps {
  open: boolean
  task: VoxnelaTaskItem | null
  tasks?: VoxnelaTaskItem[]
  onClose: () => void
  onUseTask: (task: VoxnelaTaskItem) => void
}

export const TaskShowcaseModal: React.FC<TaskShowcaseModalProps> = ({
  open,
  task,
  tasks = [],
  onClose,
  onUseTask,
}) => {
  const { locale } = useUiText()
  const studioCopy = studioMessages[locale]
  const { favoriteIds, toggleFavorite } = useStyleFavorites()
  const trackRef = useRef<HTMLDivElement>(null)
  const scrollFrameRef = useRef<number | null>(null)

  const sheetTasks = useMemo(() => {
    if (!task) return []
    const uniqueTasks = new Map<string, VoxnelaTaskItem>()
    tasks.forEach((candidate) => uniqueTasks.set(candidate.id, candidate))
    uniqueTasks.set(task.id, task)
    return Array.from(uniqueTasks.values())
  }, [task, tasks])

  const initialIndex = task
    ? Math.max(0, sheetTasks.findIndex((candidate) => candidate.id === task.id))
    : 0
  const [activeIndex, setActiveIndex] = useState(initialIndex)
  const activeTask = sheetTasks[activeIndex] ?? task

  useEffect(() => {
    if (!open || !task) return
    const nextIndex = Math.max(0, sheetTasks.findIndex((candidate) => candidate.id === task.id))
    setActiveIndex(nextIndex)
    const frame = window.requestAnimationFrame(() => {
      const track = trackRef.current
      if (track) track.scrollLeft = nextIndex * track.clientWidth
    })
    return () => window.cancelAnimationFrame(frame)
  }, [open, sheetTasks, task])

  useEffect(() => () => {
    if (scrollFrameRef.current !== null) window.cancelAnimationFrame(scrollFrameRef.current)
  }, [])

  const handleTrackScroll = () => {
    if (scrollFrameRef.current !== null) window.cancelAnimationFrame(scrollFrameRef.current)
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      const track = trackRef.current
      if (!track || track.clientWidth <= 0) return
      const nextIndex = Math.max(
        0,
        Math.min(sheetTasks.length - 1, Math.round(track.scrollLeft / track.clientWidth)),
      )
      setActiveIndex(nextIndex)
    })
  }

  const handleUse = useCallback(() => {
    if (!activeTask) return
    void haptic('success')
    onUseTask(activeTask)
  }, [activeTask, onUseTask])

  if (!activeTask) return null

  return (
    <FloatIslandSheet
      open={open}
      onClose={onClose}
      title={activeTask.title}
      subtitle={(
        <span className="task-showcase-header-meta">
          <span>{activeTask.emoji} {activeTask.badge}</span>
          <span className="task-showcase-header-ratio">
            <Ratio size={12} strokeWidth={2.2} aria-hidden="true" />
            {activeTask.aspectRatio}
          </span>
        </span>
      )}
      className="task-showcase-bottom-sheet"
    >
      <div className="task-showcase-sheet-content">
        <div
          ref={trackRef}
          className="task-showcase-carousel-track"
          onScroll={handleTrackScroll}
        >
          {sheetTasks.map((candidate, index) => (
              <div
                key={candidate.id}
                className="task-showcase-carousel-slide"
                aria-hidden={activeIndex !== index}
              >
                <div className="voxnela-task-modal-preview-wrap">
                  <ZoomableImage
                    src={candidate.sampleImageUrl}
                    alt={candidate.title}
                    isActive={activeIndex === index}
                    className="voxnela-task-modal-preview-zoom"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    imgStyle={{
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '16px',
                      boxShadow: 'none',
                    }}
                  />
                </div>
              </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="voxnela-task-modal-footer">
          <button
            type="button"
            className={`voxnela-task-modal-favorite-btn${favoriteIds.includes(activeTask.id) ? ' is-active' : ''}`}
            onClick={() => { void haptic('selection'); void toggleFavorite(activeTask.id) }}
            aria-label={favoriteIds.includes(activeTask.id) ? 'Favorilerden çıkar' : 'Favorilere ekle'}
            aria-pressed={favoriteIds.includes(activeTask.id)}
            title={favoriteIds.includes(activeTask.id) ? 'Favorilerden çıkar' : 'Favorilere ekle'}
          >
            <Heart size={22} fill={favoriteIds.includes(activeTask.id) ? 'currentColor' : 'none'} />
          </button>
          <button
            type="button"
            className="voxnela-task-modal-cta-btn"
            onClick={handleUse}
          >
            <span>{studioCopy.useStyle}</span>
            <ArrowRight size={18} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </FloatIslandSheet>
  )
}
